import React from "react";
import { PayloadAction } from "@reduxjs/toolkit";
import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { Text } from "../../../components/controls/Text";
import { Postcode } from "../../../components/controls/Postcode";
import { useDispatch, useSelector } from "../../../hooks";
import { validatePostcodeLookup } from "../slices/register.actions";

import { TYPE_DEBOUNCE } from "../../settings/common.utils";

interface CompanyAddressProps {
	onChange: (event: any) => void;
}

export const CompanyAddress = (props: CompanyAddressProps) => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();

	const companyAddress = useSelector(
		(state) => state.registration.b2b.form.companyAddress,
	);
	const postcode = useSelector((state) => state.registration.b2b.form.postcode);
	const city = useSelector((state) => state.registration.b2b.form.city);

	const country = useSelector((state) => state.settings.country);

	const {
		register,
		setValue,
		formState: { errors },
	} = useFormContext();

	const handlePostcodeLookup = useDebouncedCallback(
		async ({ target: { value } }) => {
			if (value.trim()) {
				const response: PayloadAction<{ result?: string }> = await dispatch(
					validatePostcodeLookup({
						postcode: value,
						countryCode: country,
						form: "b2b",
						target: "city",
					}),
				);

				if ("result" in response.payload) {
					setValue("city", response.payload.result, { shouldValidate: true });
				}
			}
		},
		TYPE_DEBOUNCE,
	);

	return (
		<>
			<Text
				id="companyAddress"
				className="form-input"
				ref={
					register("companyAddress", {
						value: companyAddress,
					})?.ref
				}
				error={
					typeof errors.companyAddress?.message === "string"
						? errors.companyAddress.message
						: undefined
				}
				value={companyAddress}
				onChange={(event) => {
					props.onChange(event);
				}}
				label={formatMessage({
					id: "komid.register.company.companyAddress",
				})}
			/>
			<div className="form-row">
				<div
					className="form-column"
					style={{
						flex: "1 1 40%",
						padding: "0 16px 0 0",
					}}
				>
					<Postcode
						id="postcode"
						key="postcode"
						className="form-input"
						ref={register("postcode", { value: postcode })?.ref}
						error={
							typeof errors.postcode?.message === "string"
								? errors.postcode.message
								: undefined
						}
						value={postcode}
						onChange={(event) => {
							props.onChange(event);
							handlePostcodeLookup(event);
						}}
						label={formatMessage({
							id: "komid.register.postcode",
						})}
						autoComplete="postal-code"
					/>
				</div>
				<div
					className="form-column"
					style={{
						flex: "1 1 60%",
					}}
				>
					<Text
						id="city"
						className="form-input"
						ref={register("city", { value: postcode })?.ref}
						error={
							typeof errors.city?.message === "string"
								? errors.city.message
								: undefined
						}
						value={city}
						disabled
						onChange={(event) => {
							props.onChange(event);
						}}
						label={formatMessage({
							id: "komid.register.city",
						})}
					/>
				</div>
			</div>
		</>
	);
};
